import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import { UPLOAD } from "@/constants/testIds";
import { Upload, FileText, X, CheckCircle, AlertCircle, Loader } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ANALYZING_MESSAGES = [
  "Reading your blood test PDF...",
  "Identifying blood markers...",
  "Comparing against reference ranges...",
  "Flagging abnormal values...",
  "Generating plain-language explanations...",
  "Creating medical recommendations...",
  "Finalizing your report...",
];

export default function UploadPage() {
  const [file, setFile] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [dragging, setDragging] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [msgIdx, setMsgIdx] = useState(0);
  const [error, setError] = useState("");
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const startMessageCycle = () => {
    setMsgIdx(0);
    intervalRef.current = setInterval(() => {
      setMsgIdx((i) => (i + 1) % ANALYZING_MESSAGES.length);
    }, 3000);
  };

  const stopMessageCycle = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleFile = useCallback((f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) {
      setError("Please upload a PDF file.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      setError("File too large. Maximum size is 15MB.");
      return;
    }
    setError("");
    setFile(f);
  }, []); // setError, setFile are stable state setters

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  }, [handleFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  const handleAnalyze = async () => {
    if (!file) return;
    setError("");
    setAnalyzing(true);
    startMessageCycle();

    const form = new FormData();
    form.append("file", file);
    if (patientName.trim()) form.append("patient_name", patientName.trim());

    try {
      const { data } = await axios.post(`${API}/reports/upload`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000, // 2 min timeout
      });
      stopMessageCycle();
      navigate(`/reports/${data.id}`);
    } catch (e) {
      stopMessageCycle();
      setAnalyzing(false);
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Analysis failed. Please try again.");
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={UPLOAD.page}>
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>New Analysis</h1>
          <p className="text-slate-500 text-sm mt-1">Upload a blood test PDF to generate a comprehensive report</p>
        </div>

        {/* Patient name */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 mb-5">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Patient Name <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            data-testid={UPLOAD.patientNameInput}
            type="text"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
            placeholder="e.g. John Smith"
            disabled={analyzing}
            className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all disabled:bg-slate-50"
          />
          <p className="text-xs text-slate-400 mt-1.5">Override the name extracted from the PDF</p>
        </div>

        {/* Drop zone */}
        <div
          data-testid={UPLOAD.dropZone}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !file && !analyzing && fileInputRef.current?.click()}
          className={`bg-white border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
            ${dragging ? "border-blue-500 bg-blue-50" : "border-slate-300 hover:border-blue-400 hover:bg-slate-50"}
            ${file ? "cursor-default" : "cursor-pointer"}
            ${analyzing ? "opacity-60 cursor-not-allowed" : ""}`}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />

          {!file ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                <Upload size={24} className="text-blue-900" />
              </div>
              <div>
                <p className="text-slate-700 font-medium text-sm">
                  {dragging ? "Drop your PDF here" : "Drag & drop your blood test PDF"}
                </p>
                <p className="text-slate-400 text-xs mt-1">or <span className="text-blue-900 font-medium">browse files</span> · PDF only · Max 15MB</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <FileText size={20} className="text-blue-900" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                <p className="text-xs text-slate-400">{formatSize(file.size)}</p>
              </div>
              <button
                data-testid={UPLOAD.removeFileButton}
                onClick={(e) => { e.stopPropagation(); setFile(null); setError(""); }}
                disabled={analyzing}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-40">
                <X size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div data-testid={UPLOAD.errorMessage} className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mt-4 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" /> {error}
          </div>
        )}

        {/* Analyzing state */}
        {analyzing && (
          <div data-testid={UPLOAD.analyzingState} className="bg-blue-50 border border-blue-200 rounded-lg p-5 mt-5">
            <div className="flex items-center gap-3 mb-3">
              <Loader size={18} className="text-blue-900 animate-spin" />
              <p className="text-blue-900 font-medium text-sm">Analyzing your blood test...</p>
            </div>
            <p className="text-blue-700 text-sm" data-testid={UPLOAD.analyzingMessage}>
              {ANALYZING_MESSAGES[msgIdx]}
            </p>
            <div className="mt-3 w-full bg-blue-100 rounded-full h-1.5">
              <div className="h-1.5 bg-blue-900 rounded-full animate-pulse w-3/4" />
            </div>
            <p className="text-blue-600 text-xs mt-2">This may take 20–30 seconds. Please don't close this page.</p>
          </div>
        )}

        {/* Analyze button */}
        <button
          data-testid={UPLOAD.analyzeButton}
          onClick={handleAnalyze}
          disabled={!file || analyzing}
          className="w-full mt-5 bg-blue-900 text-white py-3 rounded-lg font-medium text-sm hover:bg-blue-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
          {analyzing ? (
            <><Loader size={16} className="animate-spin" /> Analyzing...</>
          ) : (
            <><CheckCircle size={16} /> Generate Report</>
          )}
        </button>

        <p className="text-center text-xs text-slate-400 mt-4">
          Your data is processed securely and never stored beyond your account.
        </p>
      </main>
    </div>
  );
}
